import { useState, useId } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useClientPoint,
  useDelayGroup,
  UseFloatingReturn,
  Placement,
} from '@floating-ui/react';

interface UseTooltipOptions {
  content: string;
  placement?: Placement;
  followCursor?: boolean;
}

interface UseTooltipReturn {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  tooltipId: string;
  refs: UseFloatingReturn['refs'];
  floatingStyles: React.CSSProperties;
  getReferenceProps: (
    userProps?: React.HTMLProps<Element>
  ) => Record<string, unknown>;
  getFloatingProps: (
    userProps?: React.HTMLProps<HTMLElement>
  ) => Record<string, unknown>;
  content: string;
}

export function useTooltip({
  content,
  placement = 'bottom',
  followCursor = false,
}: UseTooltipOptions): UseTooltipReturn {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipId = useId();

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift({ padding: 5 }),
    ],
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, { delay, move: false });
  const clientPoint = useClientPoint(context, { enabled: followCursor });

  const focus = useFocus(context);

  const dismiss = useDismiss(context);

  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    clientPoint,
    focus,
    dismiss,
    role,
  ]);

  return {
    isOpen,
    setIsOpen,
    tooltipId,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    content,
  };
}
